import type {PayloadAction} from "@reduxjs/toolkit";
import {createSlice} from "@reduxjs/toolkit";
import {VisibleDialog} from "@/lib/dialog-orchestrator/types";

/**
 * Redux is a *mirror*, not the brain.
 *
 * The orchestrator (plain TS, outside Redux) owns all queueing/priority/flow
 * logic and pushes its visible-dialog set here via a single action. Components
 * subscribe to `state.dialogs.visible` to render. There is deliberately NO
 * business logic in this reducer — it only stores a snapshot, which keeps Redux
 * DevTools as an accurate, time-travellable view of what's on screen.
 */
export interface DialogsState {
    /** Dialogs currently on screen, ordered by ascending z-index. */
    visible: VisibleDialog[];
    /** Diagnostics for DevTools — not used for rendering. */
    queueLength: number;
    activeFlows: string[];
}

const initialState: DialogsState = {
    visible: [],
    queueLength: 0,
    activeFlows: [],
};

const dialogsSlice = createSlice({
    name: "dialogs",
    initialState,
    reducers: {
        syncVisibleDialogs(state, action: PayloadAction<VisibleDialog[]>) {
            state.visible = action.payload;
        },
        syncDialogDiagnostics(
            state,
            action: PayloadAction<{queueLength: number; activeFlows: string[]}>,
        ) {
            state.queueLength = action.payload.queueLength;
            state.activeFlows = action.payload.activeFlows;
        },
    },
});

export const {syncVisibleDialogs, syncDialogDiagnostics} = dialogsSlice.actions;
export const dialogsReducer = dialogsSlice.reducer;

// ── selectors ───────────────────────────────────────────────────────────────
export const selectVisibleDialogs = (s: {dialogs: DialogsState}) => s.dialogs.visible;
export const selectTopDialog = (s: {dialogs: DialogsState}) =>
    s.dialogs.visible[s.dialogs.visible.length - 1];
